import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Card, Button, Table, Spinner, Badge, OverlayTrigger, Tooltip, Dropdown } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { KTIcon } from '../../../../_metronic/helpers'
import { ConfirmationModal } from '../../components/ConfirmationModal'
import { GET_TWILIO_WHATSAPP_TEMPLATES } from '../../gql/queries/twilio'
import { TOGGLE_TWILIO_TEMPLATE_ACTIVE, ARCHIVE_TWILIO_TEMPLATE, DELETE_TWILIO_TEMPLATE, SUBMIT_TWILIO_TEMPLATE_FOR_APPROVAL } from '../../gql/mutations/twilio'
import { TwilioSyncDiffModal } from './TwilioSyncDiffModal'

import { TemplateConfigurationModal } from './TemplateConfigurationModal'
import { CreateTwilioTemplateModal } from './CreateTwilioTemplateModal'

const M = defineMessages({
  title: {id: 'integrations.twilio.title', defaultMessage: 'Twilio WhatsApp Templates'},
  syncedCount: {
    id: 'integrations.twilio.syncedCount',
    defaultMessage: '{count, plural, one {# synced template} other {# synced templates}}',
  },
  loading: {id: 'integrations.twilio.loading', defaultMessage: 'Loading Twilio templates...'},
  empty: {
    id: 'integrations.twilio.empty',
    defaultMessage: 'No templates found. Click "Sync" to grab existing templates from Twilio.',
  },

  actionSync: {id: 'integrations.twilio.action.sync', defaultMessage: 'Sync (Twilio API)'},
  actionCreate: {id: 'integrations.template.create', defaultMessage: 'Create template'},
  actionActivate: {id: 'integrations.twilio.action.activate', defaultMessage: 'Activate'},
  actionDeactivate: {id: 'integrations.twilio.action.deactivate', defaultMessage: 'Deactivate'},
  actionArchive: {id: 'integrations.twilio.action.archive', defaultMessage: 'Archive'},
  actionSubmit: {id: 'integrations.twilio.action.submit', defaultMessage: 'Submit'},
  actionConfirm: {id: 'integrations.twilio.action.confirm', defaultMessage: 'Confirm'},

  colName: {
    id: 'integrations.twilio.col.name',
    defaultMessage: 'Local name (Twilio SID)',
  },
  colLangCategory: {
    id: 'integrations.twilio.col.langCategory',
    defaultMessage: 'Lang / Category',
  },
  colMetaStatus: {
    id: 'integrations.twilio.col.metaStatus',
    defaultMessage: 'WhatsApp management status',
  },
  colAi: {id: 'integrations.twilio.col.ai', defaultMessage: 'AI (LLM agent)'},
  colStatus: {id: 'integrations.twilio.col.status', defaultMessage: 'PolicyTools status'},

  statusActive: {id: 'integrations.twilio.status.active', defaultMessage: 'Active'},
  statusInactive: {id: 'integrations.twilio.status.inactive', defaultMessage: 'Inactive'},

  menuConfiguration: {
    id: 'integrations.twilio.menu.configuration',
    defaultMessage: 'Configuration (AI/Vars)',
  },
  menuSubmitApproval: {
    id: 'integrations.twilio.menu.submitApproval',
    defaultMessage: 'Submit for WhatsApp approval',
  },
  menuDelete: {id: 'integrations.twilio.menu.delete', defaultMessage: 'Delete (local only)'},

  confirmDeactivateTitle: {
    id: 'integrations.template.deactivateTitle',
    defaultMessage: 'Deactivate template',
  },
  confirmActivateTitle: {
    id: 'integrations.template.activateTitle',
    defaultMessage: 'Activate template',
  },
  confirmArchiveTitle: {
    id: 'integrations.twilio.confirm.archiveTitle',
    defaultMessage: 'Archive template',
  },
  confirmDeleteTitle: {
    id: 'integrations.twilio.confirm.deleteTitle',
    defaultMessage: 'Delete template',
  },
  confirmSubmitTitle: {
    id: 'integrations.twilio.confirm.submitTitle',
    defaultMessage: 'Submit for approval',
  },
  confirmDefaultTitle: {
    id: 'integrations.twilio.confirm.defaultTitle',
    defaultMessage: 'Confirm action',
  },
  confirmDeactivateBody: {
    id: 'integrations.twilio.confirm.deactivateBody',
    defaultMessage: 'Are you sure you want to <b>deactivate</b> the template <b>"{name}"</b>?',
  },
  confirmActivateBody: {
    id: 'integrations.twilio.confirm.activateBody',
    defaultMessage: 'Are you sure you want to <b>activate</b> the template <b>"{name}"</b>?',
  },
  confirmArchiveBody: {
    id: 'integrations.twilio.confirm.archiveBody',
    defaultMessage: 'Are you sure you want to <b>archive</b> the template <b>"{name}"</b>?',
  },
  confirmArchiveHint: {
    id: 'integrations.twilio.confirm.archiveHint',
    defaultMessage: 'It will become inactive and cannot be restored from the UI.',
  },
  confirmDeleteBody: {
    id: 'integrations.twilio.confirm.deleteBody',
    defaultMessage: '<b>WARNING!</b> Permanently delete <b>"{name}"</b> from the local database?',
  },
  confirmDeleteHint: {
    id: 'integrations.twilio.confirm.deleteHint',
    defaultMessage: 'This action cannot be undone.',
  },
  confirmSubmitBody: {
    id: 'integrations.twilio.confirm.submitBody',
    defaultMessage: 'Submit <b>"{name}"</b> for WhatsApp Business approval?',
  },
  confirmSubmitHint: {
    id: 'integrations.twilio.confirm.submitHint',
    defaultMessage:
      'This will submit the template to Meta/WhatsApp for review. Status will update to "pending".',
  },

  toastLoadError: {
    id: 'integrations.twilio.toast.loadError',
    defaultMessage: 'Error loading Twilio templates',
  },
  toastStatusFailed: {
    id: 'integrations.twilio.toast.statusFailed',
    defaultMessage: 'Failed to change status',
  },
  toastArchiveFailed: {
    id: 'integrations.template.toast.archiveFailed',
    defaultMessage: 'Failed to archive template',
  },
  toastDeleteFailed: {
    id: 'integrations.template.toast.deleteFailed',
    defaultMessage: 'Failed to delete template',
  },
  toastSubmitFailed: {
    id: 'integrations.twilio.toast.submitFailed',
    defaultMessage: 'Failed to submit template for approval',
  },

  // Reusadas
  archived: {id: 'comms.campaigns.templates.archivedBadge', defaultMessage: 'Archived'},
  actions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  delete: {id: 'common.action.delete', defaultMessage: 'Delete'},
})

const bold = (chunks: React.ReactNode) => <strong>{chunks}</strong>

// i18n-ok: identificador de tipo de contenido de Twilio, no es interfaz.
const DEFAULT_CONTENT_TYPE = 'twilio/text'

export const TwilioTemplatesView = () => {
  const intl = useIntl()
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showConfigModal, setShowConfigModal] = useState<any>(null)

  // Confirmation modal states
  const [confirmAction, setConfirmAction] = useState<{
    type: 'toggle' | 'archive' | 'delete' | 'submit' | null
    template: any
  }>({ type: null, template: null })
  const [confirmLoading, setConfirmLoading] = useState(false)

  const { data, loading, refetch } = useQuery(GET_TWILIO_WHATSAPP_TEMPLATES, {
    fetchPolicy: 'cache-and-network',
    onError: (err) => toast.error(err.message || intl.formatMessage(M.toastLoadError)),
  })

  // Mutations
  const [toggleActive] = useMutation(TOGGLE_TWILIO_TEMPLATE_ACTIVE)
  const [archiveTemplate] = useMutation(ARCHIVE_TWILIO_TEMPLATE)
  const [deleteTemplate] = useMutation(DELETE_TWILIO_TEMPLATE)
  const [submitForApproval] = useMutation(SUBMIT_TWILIO_TEMPLATE_FOR_APPROVAL)

  const templatesData = useMemo(() => data?.twilioWhatsappTemplates || [], [data])

  const handleToggleActive = (template: any) => {
    setConfirmAction({ type: 'toggle', template })
  }

  const handleArchive = (template: any) => {
    setConfirmAction({ type: 'archive', template })
  }

  const handleDelete = (template: any) => {
    setConfirmAction({ type: 'delete', template })
  }

  const handleSubmitForApproval = (template: any) => {
    setConfirmAction({ type: 'submit', template })
  }

  const executeConfirmAction = async () => {
    if (!confirmAction.type || !confirmAction.template) return

    setConfirmLoading(true)
    try {
      const template = confirmAction.template

      if (confirmAction.type === 'toggle') {
        const { data: res } = await toggleActive({
          variables: { contentSid: template.contentSid, isActive: !template.isActive },
          refetchQueries: ['TwilioWhatsappTemplates'],
          awaitRefetchQueries: true
        })
        if (res?.toggleTwilioTemplateActive?.success) {
          toast.success(res.toggleTwilioTemplateActive.message)
        } else {
          toast.error(intl.formatMessage(M.toastStatusFailed))
        }
      } else if (confirmAction.type === 'archive') {
        const { data: res } = await archiveTemplate({
          variables: { contentSid: template.contentSid },
          refetchQueries: ['TwilioWhatsappTemplates'],
          awaitRefetchQueries: true
        })
        if (res?.archiveTwilioTemplate?.success) {
          toast.success(res.archiveTwilioTemplate.message)
        } else {
          toast.error(intl.formatMessage(M.toastArchiveFailed))
        }
      } else if (confirmAction.type === 'delete') {
        const { data: res } = await deleteTemplate({
          variables: { contentSid: template.contentSid },
          refetchQueries: ['TwilioWhatsappTemplates'],
          awaitRefetchQueries: true
        })
        if (res?.deleteTwilioTemplate?.success) {
          toast.success(res.deleteTwilioTemplate.message)
        } else {
          toast.error(intl.formatMessage(M.toastDeleteFailed))
        }
      } else if (confirmAction.type === 'submit') {
        const { data: res } = await submitForApproval({
          variables: { contentSid: template.contentSid },
          refetchQueries: ['TwilioWhatsappTemplates'],
          awaitRefetchQueries: true
        })
        if (res?.submitTwilioTemplateForApproval?.success) {
          toast.success(res.submitTwilioTemplateForApproval.message)
        } else {
          toast.error(intl.formatMessage(M.toastSubmitFailed))
        }
      }
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setConfirmLoading(false)
      setConfirmAction({ type: null, template: null })
    }
  }

  // Titulo, cuerpo y etiqueta del boton del modal de confirmacion. Se arma aqui
  // para no repetir la torre de ternarios tres veces dentro del JSX.
  const templateName = confirmAction.template?.friendlyName
  const isActiveTemplate = Boolean(confirmAction.template?.isActive)
  const confirmCopy =
    confirmAction.type === 'toggle'
      ? {
          title: intl.formatMessage(
            isActiveTemplate ? M.confirmDeactivateTitle : M.confirmActivateTitle
          ),
          confirmText: intl.formatMessage(
            isActiveTemplate ? M.actionDeactivate : M.actionActivate
          ),
          message: (
            <p>
              <FormattedMessage
                {...(isActiveTemplate ? M.confirmDeactivateBody : M.confirmActivateBody)}
                values={{b: bold, name: templateName}}
              />
            </p>
          ),
        }
      : confirmAction.type === 'archive'
        ? {
            title: intl.formatMessage(M.confirmArchiveTitle),
            confirmText: intl.formatMessage(M.actionArchive),
            message: (
              <>
                <p>
                  <FormattedMessage
                    {...M.confirmArchiveBody}
                    values={{b: bold, name: templateName}}
                  />
                </p>
                <p className='text-warning small mb-0'>
                  <i className='bi bi-exclamation-triangle me-2'></i>
                  {intl.formatMessage(M.confirmArchiveHint)}
                </p>
              </>
            ),
          }
        : confirmAction.type === 'delete'
          ? {
              title: intl.formatMessage(M.confirmDeleteTitle),
              confirmText: intl.formatMessage(M.delete),
              message: (
                <>
                  <p>
                    <FormattedMessage
                      {...M.confirmDeleteBody}
                      values={{
                        b: (chunks: React.ReactNode) => (
                          <strong className='text-danger'>{chunks}</strong>
                        ),
                        name: templateName,
                      }}
                    />
                  </p>
                  <p className='text-muted small mb-0'>
                    {intl.formatMessage(M.confirmDeleteHint)}
                  </p>
                </>
              ),
            }
          : confirmAction.type === 'submit'
            ? {
                title: intl.formatMessage(M.confirmSubmitTitle),
                confirmText: intl.formatMessage(M.actionSubmit),
                message: (
                  <>
                    <p>
                      <FormattedMessage
                        {...M.confirmSubmitBody}
                        values={{b: bold, name: templateName}}
                      />
                    </p>
                    <p className='text-info small mb-0'>
                      <i className='bi bi-info-circle me-2'></i>
                      {intl.formatMessage(M.confirmSubmitHint)}
                    </p>
                  </>
                ),
              }
            : {
                title: intl.formatMessage(M.confirmDefaultTitle),
                confirmText: intl.formatMessage(M.actionConfirm),
                message: null,
              }

  if (loading && templatesData.length === 0) {
    return (
      <div className="card">
        <div className="card-body py-10 text-center">
          <Spinner animation="border" />
          <div className="mt-2 text-muted">{intl.formatMessage(M.loading)}</div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-5 mt-5">
        <div>
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bolder fs-3 mb-1">
              {intl.formatMessage(M.title)}
            </span>
          </h3>
          <span className="text-muted fw-bold fs-7">
            {intl.formatMessage(M.syncedCount, {count: templatesData.length})}
          </span>
        </div>
        <div className="d-flex gx-2 gap-2">
          <Button variant="primary" size="sm" onClick={() => setShowSyncModal(true)}>
            <i className="bi bi-arrow-repeat me-1" />
            {intl.formatMessage(M.actionSync)}
          </Button>
          <Button variant="success" size="sm" onClick={() => setShowCreateModal(true)}>
            <i className="bi bi-plus me-1" />
            {intl.formatMessage(M.actionCreate)}
          </Button>
        </div>
      </div>

      <Card>
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle table-row-dashed fs-6 gy-5 mb-0 px-2 border">
            <thead>
              <tr className="text-start text-muted fw-bolder fs-7 text-uppercase gs-0">
                <th className="min-w-200px px-4">{intl.formatMessage(M.colName)}</th>
                <th className="min-w-100px">{intl.formatMessage(M.colLangCategory)}</th>
                <th className="min-w-100px text-center">{intl.formatMessage(M.colMetaStatus)}</th>
                <th className="min-w-100px text-center">{intl.formatMessage(M.colAi)}</th>
                <th className="min-w-100px text-center">{intl.formatMessage(M.colStatus)}</th>
                <th className="text-end min-w-100px px-4">{intl.formatMessage(M.actions)}</th>
              </tr>
            </thead>
            <tbody>
              {templatesData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-muted">
                    {intl.formatMessage(M.empty)}
                  </td>
                </tr>
              ) : (
                templatesData.map((t: any) => (
                  <tr key={t.contentSid} className={t.isArchived ? 'opacity-50' : ''}>
                    <td className="px-4">
                      <div className="d-flex align-items-center">
                        <div className="d-flex justify-content-start flex-column">
                          <span className="text-dark fw-bolder text-hover-primary mb-1 fs-6">
                            {t.friendlyName}
                          </span>
                          <span className="text-muted fw-bold text-muted d-block fs-7">
                            <code>{t.contentSid}</code>
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="text-dark fw-bolder d-block fs-7">{t.language}</span>
                      <span className="text-muted fw-bold d-block fs-7">
                        {t.contentType || DEFAULT_CONTENT_TYPE}
                      </span>
                    </td>
                    <td className="text-center">
                      <span 
                        className={`badge badge-light-${
                          t.approvalStatus === 'approved' ? 'success' : 
                          t.approvalStatus === 'rejected' ? 'danger' : 'warning'
                        } fw-bold text-uppercase`}
                      >
                        {t.approvalStatus}
                      </span>
                    </td>
                    <td className="text-center">
                        <span className={`badge badge-light-${t.llmInvocable ? 'success' : 'secondary'} rounded-circle p-2`}>
                          <i className={`bi ${t.llmInvocable ? 'bi-robot text-success' : 'bi-x text-muted'} fs-5`}></i>
                        </span>
                    </td>
                    <td className="text-center">
                      {t.isArchived ? (
                        <span className="badge badge-secondary">
                          {intl.formatMessage(M.archived)}
                        </span>
                      ) : (
                        <span className={`badge badge-light-${t.isActive ? 'primary' : 'warning'} fw-bold`}>
                          {intl.formatMessage(t.isActive ? M.statusActive : M.statusInactive)}
                        </span>
                      )}
                    </td>
                    <td className="text-end px-4">
                      <Dropdown>
                        <Dropdown.Toggle variant="light" size="sm" className="btn-active-light-primary">
                          {intl.formatMessage(M.actions)}
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end" popperConfig={{ strategy: 'fixed' }}>
                          <Dropdown.Item onClick={() => setShowConfigModal(t)}>
                            <KTIcon iconName="setting-2" className="me-2 text-primary" /> {intl.formatMessage(M.menuConfiguration)}
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          {!t.isArchived && t.approvalStatus !== 'approved' && (
                            <Dropdown.Item onClick={() => handleSubmitForApproval(t)} className="text-success">
                              <KTIcon iconName="send" className="me-2 text-success" /> {intl.formatMessage(M.menuSubmitApproval)}
                            </Dropdown.Item>
                          )}
                          {!t.isArchived && (
                            <Dropdown.Item onClick={() => handleToggleActive(t)}>
                              <KTIcon iconName={t.isActive ? "cross" : "check"} className="me-2" />
                              {intl.formatMessage(t.isActive ? M.actionDeactivate : M.actionActivate)}
                            </Dropdown.Item>
                          )}
                          {!t.isArchived && (
                            <Dropdown.Item onClick={() => handleArchive(t)} className="text-warning">
                              <KTIcon iconName="archive" className="me-2 text-warning" /> {intl.formatMessage(M.actionArchive)}
                            </Dropdown.Item>
                          )}
                          <Dropdown.Item onClick={() => handleDelete(t)} className="text-danger">
                            <KTIcon iconName="trash" className="me-2 text-danger" /> {intl.formatMessage(M.menuDelete)}
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <TwilioSyncDiffModal 
        show={showSyncModal} 
        onHide={() => setShowSyncModal(false)} 
        onSuccess={() => refetch()} 
      />

      <CreateTwilioTemplateModal 
        show={showCreateModal} 
        onHide={() => setShowCreateModal(false)} 
        onSuccess={() => refetch()} 
      />

      {showConfigModal && (
        <TemplateConfigurationModal
          show={!!showConfigModal}
          onHide={() => setShowConfigModal(null)}
          template={showConfigModal}
          onSuccess={() => refetch()}
          isTwilio={true}
        />
      )}

      <ConfirmationModal
        show={confirmAction.type !== null}
        onHide={() => setConfirmAction({ type: null, template: null })}
        onConfirm={executeConfirmAction}
        title={confirmCopy.title}
        message={confirmCopy.message}
        confirmText={confirmCopy.confirmText}
        variant={
          confirmAction.type === 'delete'
            ? 'danger'
            : confirmAction.type === 'submit'
            ? 'success'
            : confirmAction.type === 'archive'
            ? 'warning'
            : 'primary'
        }
        loading={confirmLoading}
      />
    </>
  )
}
